import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wrench, ArrowLeft, Mail, MessageSquare, Shield, 
  HelpCircle, User, Smartphone, AlertTriangle, Briefcase, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { api } from '../lib/api';

export default function SupportPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitContact(contactForm);
      toast.success('Message sent successfully! We\'ll get back to you within 24-48 business hours.');
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0F5156] text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">Support – Link-A-Pro</h1>
          </div>
          <p className="text-lg text-white/90">
            Welcome to Link-A-Pro Support.
          </p>
          <p className="text-white/80 mt-2">
            We are committed to providing fast, reliable assistance to ensure you have the best experience using our platform.
          </p>
        </div>
      </header>

      {/* What is Link-A-Pro */}
      <section className="py-12 bg-gray-50 border-b">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="text-3xl">📌</div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Link-A-Pro?</h2>
              <p className="text-gray-600 mb-4">
                Link-A-Pro is a professional networking and service-connection platform designed to connect individuals and businesses with trusted professionals across various industries.
              </p>
              <p className="text-gray-600">
                Whether you are hiring a professional or offering your services, our goal is to make the connection seamless, secure, and efficient.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Can We Help */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-10">
            <div className="text-3xl">🛠</div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">How Can We Help?</h2>
          </div>

          <div className="space-y-8">
            {/* 1. Account Support */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900">Account Support</h3>
              </div>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  Trouble signing in?
                </li>
                <li className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  Forgot your password?
                </li>
                <li className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  Need to update your profile information?
                </li>
                <li className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  Want to delete your account?
                </li>
              </ul>
              <div className="flex items-center gap-2 text-[#0F5156] font-medium">
                <Mail className="w-5 h-5" />
                <span>Email us at:</span>
                <a href="mailto:info@thetechxpert.com" className="hover:underline">info@thetechxpert.com</a>
              </div>
            </div>

            {/* 2. Technical Issues */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900">Technical Issues</h3>
              </div>
              <p className="text-gray-600 mb-4">If you are experiencing:</p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-400" />
                  App crashes
                </li>
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-400" />
                  Payment issues
                </li>
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-400" />
                  Messaging errors
                </li>
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-400" />
                  Profile upload problems
                </li>
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-400" />
                  Booking glitches
                </li>
              </ul>
              <div className="flex items-center gap-2 text-[#0F5156] font-medium mb-6">
                <Mail className="w-5 h-5" />
                <span>Please email:</span>
                <a href="mailto:info@thetechxpert.com" className="hover:underline">info@thetechxpert.com</a>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Please include:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Your device type (iPhone/Android)</li>
                  <li>• App version (if known)</li>
                  <li>• Screenshots (if possible)</li>
                  <li>• A brief description of the issue</li>
                </ul>
              </div>
            </div>

            {/* 3. Safety & Reporting */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600 font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900">Safety & Reporting</h3>
              </div>
              <p className="text-gray-600 mb-4 font-medium">Your safety is important to us.</p>
              <p className="text-gray-600 mb-4">If you need to:</p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-gray-400" />
                  Report inappropriate behavior
                </li>
                <li className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-gray-400" />
                  Flag suspicious activity
                </li>
                <li className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-gray-400" />
                  Report fraudulent listings
                </li>
              </ul>
              <div className="flex items-center gap-2 text-[#E07A5F] font-medium mb-4">
                <Mail className="w-5 h-5" />
                <span>Email:</span>
                <a href="mailto:info@thetechxpert.com" className="hover:underline">info@thetechxpert.com</a>
              </div>
              <p className="text-sm text-gray-500 italic">All reports are treated confidentially.</p>
            </div>

            {/* 4. Business & Partnership Inquiries */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
                  4
                </div>
                <h3 className="text-xl font-bold text-gray-900">Business & Partnership Inquiries</h3>
              </div>
              <p className="text-gray-600 mb-4">For partnerships, advertising, or enterprise solutions:</p>
              <div className="flex items-center gap-2 text-[#0F5156] font-medium">
                <Mail className="w-5 h-5" />
                <a href="mailto:info@thetechxpert.com" className="hover:underline">info@thetechxpert.com</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Hours */}
      <section className="py-12 bg-[#0F5156]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-3xl">⏱</div>
            <h2 className="text-2xl font-bold text-white">Support Hours</h2>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-white/80" />
              <span className="text-white font-medium">Monday – Friday</span>
            </div>
            <p className="text-white/90 text-lg ml-8">9:00 AM – 5:00 PM (AST)</p>
            <p className="text-white/70 mt-4 ml-8">We aim to respond within 24–48 business hours.</p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gray-50" data-testid="support-contact-section">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-12">
            <MessageSquare className="w-12 h-12 text-[#0F5156] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Send Us a Message
            </h2>
            <p className="text-gray-600">Fill out the form below and we'll get back to you</p>
          </div>

          <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-testid="support-contact-form">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="h-12"
                  data-testid="support-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="h-12"
                  data-testid="support-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">How can we help?</label>
                <Textarea
                  placeholder="Please describe your issue or question in detail..."
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={5}
                  data-testid="support-message"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0F5156] hover:bg-[#0A3A3E] text-white h-12 text-base font-medium"
                data-testid="support-submit"
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-12 bg-white border-t">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-2xl">📍</div>
            <h2 className="text-xl font-bold text-gray-900">Company Information</h2>
          </div>
          <p className="text-gray-600">
            Link-A-Pro is a product of <span className="font-semibold text-[#E07A5F]">The TechXpert</span>.
          </p>
          <p className="text-gray-500 text-sm mt-2">All rights reserved.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
              <Wrench className="w-4 h-4 text-[#0F5156]" />
            </div>
            <span className="text-lg font-bold">Link-A-Pro</span>
          </div>
          <p className="text-gray-400 mb-4">Connecting individuals and businesses with trusted professionals</p>
          <div className="flex justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Link-A-Pro. All rights reserved.</p>
          <p className="text-gray-500 text-sm mt-2">Powered by <span className="text-[#E07A5F]">The TechXpert</span></p>
        </div>
      </footer>
    </div>
  );
}
